import { Component, ErrorInfo } from "react";
import { useTranslation } from "react-i18next";
import { useTokens } from "@/design-system/hooks/useTokens";
import {
  ErrorBoundaryProps,
  ErrorBoundaryState,
  ErrorFallbackProps,
} from "./types";

/**
 * 錯誤邊界組件
 * 用於捕獲子組件樹中的 JavaScript 錯誤，並顯示備用 UI
 */
export class ErrorBoundary extends Component<
  ErrorBoundaryProps,
  ErrorBoundaryState
> {
  constructor(props: ErrorBoundaryProps) {
    super(props);
    this.state = { hasError: false };
  }

  static getDerivedStateFromError(error: Error) {
    return { hasError: true, error };
  }

  componentDidCatch(error: Error, errorInfo: ErrorInfo) {
    console.error("錯誤邊界捕獲到錯誤:", error, errorInfo);
  }

  render() {
    if (this.state.hasError) {
      if (this.props.fallback) {
        return this.props.fallback;
      }
      return <ErrorFallback error={this.state.error} />;
    }

    return this.props.children;
  }
}

// 使用從 types.ts 引入的 ErrorFallbackProps 介面

/**
 * 錯誤回退組件
 * 當錯誤邊界捕獲到錯誤時顯示的 UI
 */
function ErrorFallback({ error }: ErrorFallbackProps) {
  const { t } = useTranslation();
  const tokens = useTokens();

  return (
    <div
      className="min-h-screen flex items-center justify-center bg-white dark:bg-gray-900 p-4"
      style={{
        minHeight: "100vh",
        backgroundColor: tokens.colors.background.default,
        padding: tokens.spacing.md,
      }}
    >
      <div
        className="max-w-md w-full bg-white dark:bg-gray-800 rounded-lg shadow-lg p-6 text-center"
        style={{
          backgroundColor: tokens.colors.background.paper,
          borderRadius: tokens.radius.lg,
          boxShadow: tokens.shadows.lg,
          padding: tokens.spacing.xl,
        }}
      >
        <div className="text-red-500 text-5xl mb-4">⚠️</div>
        <h2
          className="text-xl font-bold text-gray-800 dark:text-white mb-2"
          style={{
            fontSize: tokens.typography.size.xl,
            fontWeight: tokens.typography.weight.bold,
            color: tokens.colors.text.primary,
            marginBottom: tokens.spacing.sm,
          }}
        >
          {t("status.error_occurred")}
        </h2>
        <p
          className="text-gray-600 dark:text-gray-300 mb-4"
          style={{
            color: tokens.colors.text.secondary,
            marginBottom: tokens.spacing.md,
          }}
        >
          {t("status.error_message")}
        </p>
        {error && (
          <div
            className="mt-4 p-3 bg-red-50 dark:bg-red-900/20 rounded text-left overflow-auto max-h-32"
            style={{
              marginTop: tokens.spacing.md,
              padding: tokens.spacing.sm,
              backgroundColor: tokens.colors.error.light,
              borderRadius: tokens.radius.md,
              maxHeight: "8rem",
            }}
          >
            <p
              className="text-xs text-red-800 dark:text-red-300 font-mono"
              style={{
                fontSize: tokens.typography.size.xs,
                color: tokens.colors.error.dark,
                fontFamily: tokens.typography.fontFamily.mono,
              }}
            >
              {error.message}
            </p>
          </div>
        )}
        <button
          onClick={() => window.location.reload()}
          className="mt-6 px-4 py-2 bg-blue-500 hover:bg-blue-600 text-white rounded transition-colors"
          style={{
            marginTop: tokens.spacing.lg,
            padding: `${tokens.spacing.sm} ${tokens.spacing.md}`,
            backgroundColor: tokens.colors.primary.main,
            color: tokens.colors.primary.contrastText,
            borderRadius: tokens.radius.md,
            transition: tokens.transition.normal,
          }}
        >
          {t("actions.refresh_page")}
        </button>
      </div>
    </div>
  );
}
